import { useEffect } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import Youtube from '@tiptap/extension-youtube'
import { Table } from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TextAlign from '@tiptap/extension-text-align'
import Color from '@tiptap/extension-color'
import { TextStyle } from '@tiptap/extension-text-style'
import Underline from '@tiptap/extension-underline'
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import { common, createLowlight } from 'lowlight'
import api from '../../../services/api'
import { getImageUrl } from '../../../utils/helpers'
import './TiptapEditor.css'

const lowlight = createLowlight(common)

interface TiptapEditorProps {
  value: string
  onChange: (value: string) => void
  label?: string
  required?: boolean
}

function TiptapEditor({ value, onChange, label, required }: TiptapEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Image,
      Youtube.configure({
        width: 640,
        height: 360,
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Color,
      TextStyle,
      Underline,
      CodeBlockLowlight.configure({ lowlight }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
  })

  // Sync external value changes (e.g. when article loads after mount)
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value)
    }
  }, [editor, value])

  if (!editor) return null

  const addLink = () => {
    const url = window.prompt('URL du lien :')
    if (!url) return
    editor.chain().focus().setLink({ href: url }).run()
  }

  const addImage = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return
      try {
        const data = await api.uploadFile<{ file_path: string }>('/media/upload', file)
        const url = getImageUrl(data.file_path)
        editor.chain().focus().setImage({ src: url }).run()
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Erreur upload image')
      }
    }
    input.click()
  }

  const addYoutube = () => {
    const url = window.prompt('URL YouTube :')
    if (!url) return
    editor.commands.setYoutubeVideo({ src: url })
  }

  const addTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
  }

  return (
    <div className="tiptap-editor">
      {label && (
        <label className="tiptap-label">
          {label}
          {required && <span className="required">*</span>}
        </label>
      )}

      <div className="tiptap-toolbar">
        <div className="toolbar-group">
          <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={editor.isActive('heading', { level: 1 }) ? 'active' : ''} title="Titre 1">H1</button>
          <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={editor.isActive('heading', { level: 2 }) ? 'active' : ''} title="Titre 2">H2</button>
          <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={editor.isActive('heading', { level: 3 }) ? 'active' : ''} title="Titre 3">H3</button>
        </div>

        <div className="toolbar-separator" />

        <div className="toolbar-group">
          <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'active' : ''} title="Gras"><strong>B</strong></button>
          <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'active' : ''} title="Italique"><em>I</em></button>
          <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} className={editor.isActive('underline') ? 'active' : ''} title="Souligné"><u>U</u></button>
          <button type="button" onClick={() => editor.chain().focus().toggleStrike().run()} className={editor.isActive('strike') ? 'active' : ''} title="Barré"><s>S</s></button>
        </div>

        <div className="toolbar-separator" />

        <div className="toolbar-group">
          <button type="button" onClick={() => editor.chain().focus().setTextAlign('left').run()} className={editor.isActive({ textAlign: 'left' }) ? 'active' : ''} title="Aligner à gauche">↤</button>
          <button type="button" onClick={() => editor.chain().focus().setTextAlign('center').run()} className={editor.isActive({ textAlign: 'center' }) ? 'active' : ''} title="Centrer">↔</button>
          <button type="button" onClick={() => editor.chain().focus().setTextAlign('right').run()} className={editor.isActive({ textAlign: 'right' }) ? 'active' : ''} title="Aligner à droite">↦</button>
          <button type="button" onClick={() => editor.chain().focus().setTextAlign('justify').run()} className={editor.isActive({ textAlign: 'justify' }) ? 'active' : ''} title="Justifier">⊞</button>
        </div>

        <div className="toolbar-separator" />

        <div className="toolbar-group">
          <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive('bulletList') ? 'active' : ''} title="Liste à puces">• Liste</button>
          <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={editor.isActive('orderedList') ? 'active' : ''} title="Liste numérotée">1. Liste</button>
          <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={editor.isActive('blockquote') ? 'active' : ''} title="Citation">❝</button>
          <button type="button" onClick={() => editor.chain().focus().toggleCodeBlock().run()} className={editor.isActive('codeBlock') ? 'active' : ''} title="Bloc de code">Code</button>
          <button type="button" onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Séparateur">―</button>
        </div>

        <div className="toolbar-separator" />

        <div className="toolbar-group">
          <button type="button" onClick={addLink} className={editor.isActive('link') ? 'active' : ''} title="Lien">🔗</button>
          <button type="button" onClick={addImage} title="Image">🖼</button>
          <button type="button" onClick={addYoutube} title="Vidéo YouTube">▶</button>
          <button type="button" onClick={addTable} title="Tableau">▦</button>
        </div>

        <div className="toolbar-separator" />

        <div className="toolbar-group">
          <button type="button" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Annuler">↩</button>
          <button type="button" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Rétablir">↪</button>
        </div>

        <div className="toolbar-group toolbar-color">
          <input type="color" onChange={(e) => editor.chain().focus().setColor(e.target.value).run()} title="Couleur du texte" />
        </div>
      </div>

      <EditorContent editor={editor} className="tiptap-content" />
    </div>
  )
}

export default TiptapEditor
